/**
 * UI Builder
 * Handles conditional UI rendering for the Process Visualization Widget
 */
export class UIBuilder {
    constructor(container, config) {
        this.container = typeof container === 'string' ? document.getElementById(container) : container;
        this.config = config;
        this.elements = {};
        
        if (!this.container) {
            throw new Error('Container element not found');
        }
    }

    /**
     * Build the complete UI interface
     */
    buildInterface() {
        // Clear existing content
        this.container.innerHTML = '';
        
        // Add main container class for scoping
        this.container.classList.add(this.config.ui.containerClass);

        this.elements.main = this.createMain();
        this.container.appendChild(this.elements.main);

        return this.elements;
    }

    /**
     * Create filter controls
     */
    createFilterControls() {
        const filterContainer = document.createElement('div');
        filterContainer.className = 'pv-filter-container';

        // Edge filter
        const edgeFilterGroup = document.createElement('div');
        edgeFilterGroup.className = 'pv-filter-controls';

        const edgeFilterId = `edge-filter-${this.container.id}`;
        const edgeFilterValueId = `edge-filter-value-${this.container.id}`;

        const edgeLabel = document.createElement('label');
        edgeLabel.setAttribute('for', edgeFilterId);
        edgeLabel.textContent = 'Show top connections:';
        edgeFilterGroup.appendChild(edgeLabel);

        const edgeSlider = document.createElement('input');
        edgeSlider.type = 'range';
        edgeSlider.id = edgeFilterId;
        edgeSlider.min = '0';
        edgeSlider.max = '100';
        edgeSlider.value = this.config.filters.defaultEdgeFilter.toString();
        edgeSlider.step = '1';
        edgeFilterGroup.appendChild(edgeSlider);

        const edgeValue = document.createElement('span');
        edgeValue.id = edgeFilterValueId;
        edgeValue.textContent = `${this.config.filters.defaultEdgeFilter}%`;
        edgeFilterGroup.appendChild(edgeValue);

        filterContainer.appendChild(edgeFilterGroup);

        // Node filter
        const nodeFilterGroup = document.createElement('div');
        nodeFilterGroup.className = 'pv-filter-controls';

        const nodeFilterId = `node-filter-${this.container.id}`;
        const nodeFilterValueId = `node-filter-value-${this.container.id}`;

        const nodeLabel = document.createElement('label');
        nodeLabel.setAttribute('for', nodeFilterId);
        nodeLabel.textContent = 'Show top activities:';
        nodeFilterGroup.appendChild(nodeLabel);

        const nodeSlider = document.createElement('input');
        nodeSlider.type = 'range';
        nodeSlider.id = nodeFilterId;
        nodeSlider.min = '0';
        nodeSlider.max = '100';
        nodeSlider.value = this.config.filters.defaultNodeFilter.toString();
        nodeSlider.step = '1';
        nodeFilterGroup.appendChild(nodeSlider);

        const nodeValue = document.createElement('span');
        nodeValue.id = nodeFilterValueId;
        nodeValue.textContent = `${this.config.filters.defaultNodeFilter}%`;
        nodeFilterGroup.appendChild(nodeValue);

        filterContainer.appendChild(nodeFilterGroup);

        return filterContainer;
    }

    /**
     * Create zoom controls
     */
    createZoomControls() {
        const zoomContainer = document.createElement('div');
        zoomContainer.className = 'pv-zoom-controls';

        const zoomInId = `zoom-in-${this.container.id}`;
        const zoomOutId = `zoom-out-${this.container.id}`;

        const zoomInBtn = document.createElement('button');
        zoomInBtn.id = zoomInId;
        zoomInBtn.textContent = '+';
        zoomInBtn.className = 'pv-zoom-btn';
        zoomContainer.appendChild(zoomInBtn);

        const zoomLabel = document.createElement('span');
        zoomLabel.textContent = 'Zoom';
        zoomLabel.className = 'pv-zoom-label';
        zoomContainer.appendChild(zoomLabel);

        const zoomOutBtn = document.createElement('button');
        zoomOutBtn.id = zoomOutId;
        zoomOutBtn.textContent = '-';
        zoomOutBtn.className = 'pv-zoom-btn';
        zoomContainer.appendChild(zoomOutBtn);

        return zoomContainer;
    }

    /**
     * Create main content area
     */
    createMain() {
        const main = document.createElement('main');
        main.className = 'pv-main';

        // Graph container
        const graphContainer = document.createElement('div');
        graphContainer.id = `graph-container-${this.container.id}`;
        graphContainer.className = 'pv-graph-container';
        main.appendChild(graphContainer);

        // Sidebar if any of its content is enabled
        if (this.config.ui.showMetrics || this.config.ui.showFilters || this.config.ui.showZoomControls) {
            const sidebar = this.createSidebar();
            main.appendChild(sidebar);
        }

        return main;
    }


    /**
     * Create Sidebar
     */
    createSidebar() {
        const sidebarId = `sidebar-${this.container.id}`;

        const sidebar = document.createElement('div');
        sidebar.id = sidebarId;
        sidebar.className = 'pv-sidebar';

        if (this.config.ui.showFilters || this.config.ui.showZoomControls) {

            const sidebarControlsId = `sidebar-controls-${this.container.id}`;
            const sidebarControls = document.createElement('div');
            sidebarControls.id = sidebarControlsId;
            sidebarControls.className = 'pv-sidebar-controls';

            if (this.config.ui.showZoomControls) {
                const zoomControls = this.createZoomControls();
                sidebarControls.appendChild(zoomControls);
                sidebarControls.appendChild(document.createElement('hr'));
            }

            if (this.config.ui.showFilters) {
                const filterControls = this.createFilterControls();
                sidebarControls.appendChild(filterControls);
                sidebarControls.appendChild(document.createElement('hr'));
            }

            sidebar.appendChild(sidebarControls);

        }

        if (this.config.ui.showMetrics) {
            const metricsPanel = this.createMetricsPanel();
            sidebar.appendChild(metricsPanel);
        }

        return sidebar;
    }

    createMetricsPanel() {
        const metricsId = `metrics-${this.container.id}`;
        const metricsContentId = `metrics-content-${this.container.id}`;

        const metricsPanel = document.createElement('div');
        metricsPanel.id = metricsId;
        metricsPanel.className = 'pv-metrics-panel';

        const title = document.createElement('h2');
        title.textContent = 'Process Metrics';
        title.className = 'pv-metrics-title';
        metricsPanel.appendChild(title);

        const content = document.createElement('div');
        content.id = metricsContentId;
        content.className = 'pv-metrics-content';
        metricsPanel.appendChild(content);

        return metricsPanel;
    }

    /**
     * Show loading indicator
     */
    showLoading(message = null) {
        const loadingMessage = message || this.config.messages.loading;
        
        // Change cursor to indicate loading
        this.container.style.cursor = 'progress';
        
        // Create or update loading overlay
        let loadingOverlay = this.container.querySelector('.pv-loading-overlay');
        if (!loadingOverlay) {
            loadingOverlay = document.createElement('div');
            loadingOverlay.className = 'pv-loading-overlay';
            
            const spinner = document.createElement('div');
            spinner.className = 'pv-loading-spinner';
            loadingOverlay.appendChild(spinner);
            
            const text = document.createElement('div');
            text.className = 'pv-loading-text';
            text.textContent = loadingMessage;
            loadingOverlay.appendChild(text);
            
            this.container.appendChild(loadingOverlay);
        } else {
            loadingOverlay.querySelector('.pv-loading-text').textContent = loadingMessage;
            loadingOverlay.style.display = 'flex';
        }
    }

    /**
     * Hide loading indicator
     */
    hideLoading() {
        this.container.style.cursor = 'default';
        
        const loadingOverlay = this.container.querySelector('.pv-loading-overlay');
        if (loadingOverlay) {
            loadingOverlay.style.display = 'none';
        }
    }

    /**
     * Show message to user
     */
    showMessage(message, type = 'info', duration = 3000) {
        // Create message element if it doesn't exist
        let messageEl = this.container.querySelector('.pv-message');
        if (!messageEl) {
            messageEl = document.createElement('div');
            messageEl.className = 'pv-message';

            let graphContainer = this.container.querySelector('.pv-graph-container');
            graphContainer.appendChild(messageEl);
        }

        // Set message type class
        messageEl.className = `pv-message pv-message-${type}`;
        
        // Set message content
        messageEl.textContent = message;
        messageEl.style.opacity = '1';
        messageEl.style.display = 'block';
        
        // Auto-hide after specified duration
        if (duration > 0) {
            setTimeout(() => {
                messageEl.style.opacity = '0';
                setTimeout(() => {
                    messageEl.style.display = 'none';
                }, 300);
            }, duration);
        }
    }

    /**
     * Hide message
     */
    hideMessage() {
        const messageEl = this.container.querySelector('.pv-message');
        if (messageEl) {
            messageEl.style.opacity = '0';
            setTimeout(() => {
                messageEl.style.display = 'none';
            }, 300);
        }
    }

    /**
     * Get UI element by key
     */
    getElement(key) {
        return this.elements[key];
    }

    /**
     * Update configuration and rebuild if necessary
     */
    updateConfig(newConfig) {
        const oldConfig = this.config;
        this.config = newConfig;
        
        // Check if UI structure needs to be rebuilt
        const structuralChanges = [
            'ui.showFilters',
            'ui.showMetrics',
            'ui.showZoomControls'
        ];
        
        const needsRebuild = structuralChanges.some(path => {
            const oldValue = this.getConfigValue(oldConfig, path);
            const newValue = this.getConfigValue(newConfig, path);
            return oldValue !== newValue;
        });
        
        if (needsRebuild) {
            this.buildInterface();
        }
    }

    /**
     * Helper to get nested config value
     */
    getConfigValue(config, path) {
        return path.split('.').reduce((obj, key) => obj && obj[key], config);
    }
}